import React, { useEffect, useRef, useReducer } from 'react'
import styled from 'styled-components'
import { motion, AnimatePresence } from 'framer-motion'
import { B_NUM, I_NUM, N_NUM, G_NUM, O_NUM } from './CardNumbersMap'
import BallNumber from './BallNumber'

const BingoBallWrap = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 250px;
  padding: 10px;
  background: #f7f7f7;
  box-shadow: 0 -2px 6px #ccc;

  @media print {
    display: none;
  }
`

const Controls = styled.div`
  margin: 0 0 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 500px) {
    display: block;
  }
`

const GenericButton = styled.div`
  text-align: center;

  @media print {
    display: none;
  }

  > button {
    margin: 0 3px 8px 0;
    padding: 6px 16px;

    &.pause {
      background: #ff9800;
    }

    &.stop {
      background: darkred;
    }

    &.autoplay {
      background: rebeccapurple;
    }

    &.speaker {
      background: steelblue;
    }
  }
`

const SelectInterval = styled(motion.div)`
  display: flex;
  justify-content: center;
  font-size: 13px;
`

const BallCountdown = styled(motion.div)`
  display: flex;
  justify-content: center;
  align-items: center;

  > span {
    margin-left: 5px;
    padding: 3px 10px;
    background: var(--main-color);
    color: #fff;
  }
`

function useInterval(callback, delay) {
  const savedCallback = useRef()

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

const COMPLETE_NUM = [
  ...B_NUM.map((n) => `B - ${n}`),
  ...I_NUM.map((n) => `I - ${n}`),
  ...N_NUM.map((n) => `N - ${n}`),
  ...G_NUM.map((n) => `G - ${n}`),
  ...O_NUM.map((n) => `O - ${n}`),
]

const initialState = {
  result: [],
  isRunning: false,
  ballInterval: 4,
  ballIntervalTimer: 4,
  init: true,
  isBallLastNumber: true,
  autoPlay: false,
  isSpeaking: true,
  speakingSupport: true,
}

function reducer(state, action) {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        init: action.payload,
      }

    case 'RUNNING':
      return {
        ...state,
        isRunning: action.payload,
      }

    case 'START':
      return {
        ...state,
        isRunning: true,
      }

    case 'PAUSE':
      return {
        ...state,
        isRunning: false,
        init: true,
        ballInterval: state.ballInterval,
      }

    case 'STOP':
      return {
        ...initialState,
        isSpeaking: state.isSpeaking,
        ballIntervalTimer: state.ballInterval,
      }

    case 'NEW_RESULT':
      return {
        ...state,
        result: action.payload,
      }

    case 'TOGGLE_AUTOPLAY':
      return {
        ...state,
        autoPlay: !state.autoPlay,
      }

    case 'BALL_LAST_NUMBER':
      return {
        ...state,
        isBallLastNumber: action.payload,
      }

    case 'CHANGE_INTERVAL':
      return {
        ...state,
        ballInterval: action.payload,
        ballIntervalTimer: action.payload,
      }

    case 'CHANGE_BALL_INTERVAL':
      return {
        ...state,
        ballInterval: action.payload,
      }

    case 'CHANGE_BALL_INTERVAL_TIMER':
      return {
        ...state,
        ballIntervalTimer: action.payload,
      }

    case 'BALL_EMPTY':
      return {
        ...state,
        isRunning: false,
        isBallLastNumber: false,
      }

    case 'TOGGLE_SPEAKING':
      return {
        ...state,
        isSpeaking: !state.isSpeaking,
      }

    case 'SPEAKING_SUPPORT':
      return {
        ...state,
        speakingSupport: action.payload,
      }

    default:
      return {
        ...state,
      }
  }
}

function Ball({
  visibility,
  setIsBallSet,
  unsetIsBallSet,
  isBallSet,
  setIsAutoPlayingDispatch,
  resetCheckbox,
}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const allNumber = useRef([...COMPLETE_NUM])

  const shuffle = (array) => {
    var currentIndex = array.length,
      temporaryValue,
      randomIndex

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex -= 1

      // And swap it with the current element.
      temporaryValue = array[currentIndex]
      array[currentIndex] = array[randomIndex]
      array[randomIndex] = temporaryValue
    }

    return array
  }

  const cleanNumber = (n) => n.slice(4, n.length)

  const checkAllCards = (num) => {
    const n = cleanNumber(num)
    document.querySelector(`#ctl-${n}`).checked = true
    document.querySelectorAll(`.num-${n}`).forEach((i) => (i.checked = true))
  }

  const speak = (txt) => {
    let synthesis = window.speechSynthesis

    // Get the first `en` language voice in the list
    let voice = synthesis.getVoices().filter((voice) => {
      return voice.lang === 'en-US'
    })[0]

    // Create an utterance object
    let utterance = new SpeechSynthesisUtterance(txt)
    // Set utterance properties
    utterance.voice = voice
    utterance.pitch = 1
    utterance.rate = 0.8
    utterance.volume = 1

    // Speak the utterance
    synthesis.speak(utterance)
  }

  const generateNumber = () => {
    if (!allNumber.current.length) {
      dispatch({ type: 'RUNNING', payload: false })
      return
    }
    const random = Math.floor(Math.random() * allNumber.current.length)
    const number = shuffle(allNumber.current)[random]
    if (state.isSpeaking) speak(number)
    if (state.autoPlay) checkAllCards(number)
    allNumber.current.splice(random, 1)
    if (allNumber.current.length === 0) {
      dispatch({ type: 'BALL_EMPTY' })
    }
    return `${number}`
  }

  useInterval(
    () => {
      if (!allNumber.current.length) return
      const newResult = [generateNumber(), ...state.result]
      dispatch({ type: 'NEW_RESULT', payload: newResult })
      if (state.init) dispatch({ type: 'INITIALIZE', payload: false })
    },
    state.isRunning ? (state.init ? 300 : state.ballInterval * 1000) : null
  )

  useInterval(
    () => {
      if (state.ballIntervalTimer === 1) {
        dispatch({
          type: 'CHANGE_BALL_INTERVAL_TIMER',
          payload: state.ballInterval,
        })
        return
      }
      dispatch({
        type: 'CHANGE_BALL_INTERVAL_TIMER',
        payload: state.ballIntervalTimer - 1,
      })
    },
    state.isRunning ? (state.init ? 700 : 1000) : null
  )

  const handleStart = () => {
    dispatch({ type: 'START' }) // Set running flag for buttons disabled and showing of interval controls
    setIsBallSet() // Set flag when showing Ball component
    if (!isBallSet) {
      resetCheckbox()
    }
  }

  const handleStop = () => {
    resetBall()
    unsetIsBallSet() // Set flag when showing Ball component
    dispatch({ type: 'STOP' })
  }

  const handleWinner = () => {
    dispatch({ type: 'PAUSE' })
    speak('And we have a winner!')
  }

  const handleAutoPlay = () => {
    dispatch({ type: 'TOGGLE_AUTOPLAY' })
    setIsAutoPlayingDispatch({ type: 'SET_AUTOPLAY', payload: !state.autoPlay })
    state.result.map((n) => checkAllCards(n))
  }

  const resetBall = () => {
    allNumber.current = [...COMPLETE_NUM]
  }

  useEffect(() => {
    resetBall()
    if (!('speechSynthesis' in window)) {
      alert('Voice not supported')
      dispatch({ type: 'SPEAKING_SUPPORT', payload: false })
    }
  }, [])

  return (
    <BingoBallWrap
      initial={{ opacity: 0, y: 250 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 250 }}
    >
      <Controls>
        <GenericButton>
          {state.isBallLastNumber ? (
            state.isRunning ? (
              <button
                onClick={() => dispatch({ type: 'PAUSE' })}
                disabled={!state.isRunning}
                className="pause"
              >
                Pause
              </button>
            ) : (
              <button onClick={handleStart}>
                {isBallSet ? 'Resume' : 'Start'}
              </button>
            )
          ) : (
            ''
          )}
          {isBallSet && (
            <button onClick={handleStop} className="stop">
              Stop/Reset
            </button>
          )}

          {isBallSet && <button onClick={handleWinner}>Win</button>}
          {!isBallSet && (
            <button onClick={handleAutoPlay} className="autoplay">
              {!state.autoPlay ? 'Auto-Play ON' : 'Auto-Play OFF'}
            </button>
          )}
          {state.speakingSupport && (
            <button
              onClick={() => dispatch({ type: 'TOGGLE_SPEAKING' })}
              className="speaker"
            >
              {state.isSpeaking ? 'MUTE' : 'SPEAK'}
            </button>
          )}
        </GenericButton>
        <AnimatePresence>
          {!state.isRunning ? (
            <SelectInterval
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 50, opacity: 0 }}
            >
              <label htmlFor="">
                Select interval in seconds{' '}
                <select
                  onChange={(e) =>
                    dispatch({
                      type: 'CHANGE_INTERVAL',
                      payload: e.target.value,
                    })
                  }
                  defaultValue={state.ballInterval}
                >
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="6">6</option>
                  <option value="7">7</option>
                  <option value="8">8</option>
                </select>
              </label>
            </SelectInterval>
          ) : (
            <BallCountdown
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 50, opacity: 0 }}
            >
              Next ball in <span>{state.ballIntervalTimer}</span>
            </BallCountdown>
          )}
        </AnimatePresence>
      </Controls>
      <BallNumber result={state.result} />
    </BingoBallWrap>
  )
}

export default React.memo(Ball)
